import cx from "classnames";
import type { HTMLAttributes } from "react";
import { useEffect, useRef } from "react";

import Animation from "metabase/css/core/animation.module.css";
import { useSelector } from "metabase/redux";
import { getIsNavbarOpen } from "metabase/selectors/app";
import { type BoxProps, Flex, Portal, Text } from "metabase/ui";

import { BulkActionsToast, ToastCard } from "./BulkActionBar.styled";

type BulkActionsProps = {
  opened: boolean;
  message: string;
  children: React.ReactNode | React.ReactNode[];
  className?: string;
  isNavbarOpen?: boolean;
};

/**
 * A generic floating notification that appears at the bottom of the screen with a message and
 * children that is generally used when multiple items have been selected and you need a UI element
 * to perform actions on those items.
 *
 * @param {boolean} opened  - Whether the notification is open or not
 * @param {string} message  - The message to display in the notification
 * @param {any} children    - The children to display in the notification, meant to be used with BulkActionButton components.
 * @returns
 */
export const BulkActionBar = (props: BulkActionsProps) => {
  const isNavbarOpen = useSelector(getIsNavbarOpen);
  return <BulkActionBarPortal {...props} isNavbarOpen={isNavbarOpen} />;
};

export const BulkActionBarPortal = ({
  opened,
  message,
  children,
  className,
  isNavbarOpen = true,
  ...props
}: BulkActionsProps & BoxProps & HTMLAttributes<HTMLDivElement>) => {
  const toastRef = useRef<HTMLDivElement>(null);
  // Whatever had focus right before this bar first appeared (typically the
  // control the user used to select an item), so it can be restored once
  // the bar goes away for good.
  const triggerElementRef = useRef<HTMLElement | null>(null);
  const wasOpenedRef = useRef(opened);

  useEffect(() => {
    if (opened && !wasOpenedRef.current) {
      const activeElement = document.activeElement;
      triggerElementRef.current =
        activeElement instanceof HTMLElement && activeElement !== document.body
          ? activeElement
          : null;
    }

    const isClosing = !opened && wasOpenedRef.current;
    const isReopening = opened && !wasOpenedRef.current;
    wasOpenedRef.current = opened;

    if (!isClosing && !isReopening) {
      return;
    }

    // Deferred, matching Mantine's own useFocusReturn (which restores focus
    // after a short delay for exactly this reason): gives any other
    // component's own focus transition that fired in the same commit — e.g.
    // a confirmation dialog this bar's own action opened, moving focus into
    // itself, or closed, attempting its own (possibly unsuccessful) restore
    // — a chance to settle first, so this doesn't fight it or act on stale
    // information about what currently has focus.
    const timeoutId = window.setTimeout(() => {
      const activeElement = document.activeElement;
      const nothingElseIsFocused =
        activeElement === document.body || activeElement == null;

      if (!nothingElseIsFocused) {
        return;
      }

      if (isClosing) {
        // This bar's own buttons have just unmounted. Without this, focus
        // silently drops to <body> once they're gone (WCAG 2.4.3) — restore
        // it to whatever had focus before the bar opened, if that's still
        // around.
        if (triggerElementRef.current?.isConnected) {
          triggerElementRef.current.focus();
        }
      } else {
        // Covers this bar reappearing after a nested confirmation dialog
        // closes without completing its action (e.g. Cancel) and leaves
        // nothing focused — without this, focus silently stays lost even
        // though the bar (and its buttons) are visible again (WCAG 2.4.3).
        toastRef.current?.focus();
      }
    }, 10);

    return () => window.clearTimeout(timeoutId);
  }, [opened]);

  if (!opened) {
    return null;
  }
  return (
    <Portal>
      <BulkActionsToast
        isNavbarOpen={isNavbarOpen}
        className={cx(className, Animation.popToast)}
      >
        <ToastCard
          data-testid="toast-card"
          tabIndex={-1}
          ref={toastRef}
          {...props}
        >
          {message && <Text color="text-primary-inverse">{message}</Text>}
          <Flex gap="sm" align="center">
            {children}
          </Flex>
        </ToastCard>
      </BulkActionsToast>
    </Portal>
  );
};
